import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { NzNotificationComponent, NzNotificationService } from 'ng-zorro-antd/notification';
import { ThongBaoService } from '../../services/thong-bao.service';
import { ThongBaoItem } from '../../models/thong-bao.model';
import { KetQuaGuiThuPush, WebPushService } from '../../services/web-push.service';
import { SHOW_TEST_PUSH_BUTTON } from '../../constants/app.constants';

const KICH_THUOC_TRANG = 20;
/** Còn cách đáy bấy nhiêu px thì tải trang kế tiếp. */
const NGUONG_CUON_PX = 40;

/** Bấm "Để sau" thì không mời bật thông báo trình duyệt lại trong khoảng này. */
const HOAN_MOI_BAT_MS = 7 * 24 * 60 * 60 * 1000;
const KHOA_HOAN_MOI_BAT = 'fcm_web_moi_bat_sau';
const KEY_LOI_MOI = 'web-push-loi-moi';
/** Chờ trước khi gửi thử để kịp chuyển sang tab/cửa sổ khác - tab đang mở thì Chrome không hiện popup hệ thống. */
const CHO_GUI_THU_GIAY = 5;

const ROLES_QUY_TRINH = ['ROLE_CHUYEN_VIEN_TD', 'ROLE_THAM_DINH_VIEN', 'ROLE_TDV_KY', 'ROLE_GIAM_DOC_NV', 'ROLE_ADMIN'];

@Component({
  selector: 'app-notification-bell',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './notification-bell.component.html',
  styleUrls: ['./notification-bell.component.css']
})
export class NotificationBellComponent implements OnInit, OnDestroy {
  /** Navbar truyền hàm kiểm tra role để dùng chung logic đọc role của nó. */
  @Input() kiemTraRole: (role: string) => boolean = () => false;
  @Output() collapseNavbar = new EventEmitter<void>();

  private thongBaoService = inject(ThongBaoService);
  private router = inject(Router);
  private webPushService = inject(WebPushService);
  private notification = inject(NzNotificationService);

  @ViewChild('loiMoiBatThongBao', { static: true }) loiMoiBatThongBao!: TemplateRef<{ $implicit: NzNotificationComponent; data: any }>;

  isOpen = false;
  soChuaDoc = 0;
  items: ThongBaoItem[] = [];
  page = 0;
  dangTai = false;
  hetTrang = false;
  coLoi = false;
  dangDanhDauTatCa = false;
  /** Chỉ hiện khi trình duyệt hỗ trợ và người dùng chưa chọn cho phép/chặn. */
  hienNutBatThongBao = false;
  /** Người dùng/trình duyệt đã chặn quyền: web không xin lại được, chỉ hướng dẫn mở trong cài đặt trang. */
  thongBaoBiChan = false;
  dangBatThongBao = false;
  /** Chỉ môi trường dev (environment.showTestPushButton). */
  readonly hienNutGuiThu = SHOW_TEST_PUSH_BUTTON;
  /** Số giây còn lại trước khi gửi thử; null = không đang chờ. */
  demNguocGuiThu: number | null = null;
  private demNguocTimer?: ReturnType<typeof setInterval>;

  private soChuaDocSub?: Subscription;
  private taiSub?: Subscription;

  ngOnInit(): void {
    this.soChuaDocSub = this.thongBaoService.soChuaDoc$.subscribe(so => (this.soChuaDoc = so));
    this.thongBaoService.batDauTheoDoi();
    // Component chỉ render khi đã đăng nhập nên đây là lúc đăng ký token web push (cả đăng nhập lẫn F5).
    this.webPushService.dangKyNeuDaCoQuyen();
    this.capNhatNutBatThongBao(true);
  }

  ngOnDestroy(): void {
    // Component chỉ render khi đã đăng nhập; bị hủy (đăng xuất) thì dừng poll.
    this.notification.remove(KEY_LOI_MOI);
    if (this.demNguocTimer) clearInterval(this.demNguocTimer);
    this.soChuaDocSub?.unsubscribe();
    this.taiSub?.unsubscribe();
    this.thongBaoService.dungTheoDoi();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.notification-bell')) {
      this.isOpen = false;
    }
  }

  get nhanBadge(): string {
    return this.soChuaDoc > 99 ? '99+' : String(this.soChuaDoc);
  }

  toggle(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.thongBaoService.capNhatSoChuaDoc();
      this.taiLai();
    }
  }

  taiLai(): void {
    this.taiSub?.unsubscribe();
    this.items = [];
    this.page = 0;
    this.hetTrang = false;
    this.dangTai = false;
    this.taiTrang(0);
  }

  onScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - NGUONG_CUON_PX) {
      this.taiTrangKe();
    }
  }

  taiTrangKe(): void {
    if (this.dangTai || this.hetTrang || this.coLoi) return;
    this.taiTrang(this.page + 1);
  }

  /** Lỗi ở trang đầu thì tải lại từ đầu, lỗi khi cuộn thì thử lại đúng trang kế tiếp. */
  thuLai(): void {
    this.coLoi = false;
    if (this.items.length) {
      this.taiTrangKe();
    } else {
      this.taiLai();
    }
  }

  private taiTrang(page: number): void {
    this.dangTai = true;
    this.coLoi = false;
    this.taiSub = this.thongBaoService.layDanhSach(page, KICH_THUOC_TRANG).subscribe({
      next: res => {
        const moi = res?.content ?? [];
        // Thông báo mới đến giữa lúc cuộn làm lệch trang; bỏ bản ghi đã có để không hiện trùng.
        const daCo = new Set(this.items.map(i => i.id));
        this.items = [...this.items, ...moi.filter(i => !daCo.has(i.id))];
        this.page = page;
        this.hetTrang = res?.last ?? moi.length < KICH_THUOC_TRANG;
        this.dangTai = false;
      },
      error: () => {
        this.dangTai = false;
        this.coLoi = true;
      }
    });
  }

  batThongBaoTrinhDuyet(): void {
    if (this.dangBatThongBao) return;
    this.dangBatThongBao = true;
    this.notification.remove(KEY_LOI_MOI);
    this.webPushService.xinQuyenVaDangKy().then(ok => {
      if (ok) {
        this.notification.success('Đã bật thông báo', 'Bạn sẽ nhận thông báo hồ sơ ngay trên trình duyệt.');
      } else if (this.webPushService.quyenHienTai() === 'denied') {
        this.notification.warning('Thông báo đang bị chặn',
          'Bấm biểu tượng bên trái thanh địa chỉ → Notifications → Allow để bật lại.');
      }
    }).finally(() => {
      this.dangBatThongBao = false;
      this.capNhatNutBatThongBao();
    });
  }

  /**
   * Dev: đếm ngược vài giây (để người test chuyển sang tab khác) rồi nhờ backend gửi push thử cho chính
   * mình, sau đó hiện kết quả từng khúc: quyền trình duyệt, token, FCM.
   */
  guiThongBaoThu(): void {
    if (this.demNguocGuiThu !== null) return;
    this.demNguocGuiThu = CHO_GUI_THU_GIAY;
    this.demNguocTimer = setInterval(() => {
      if (this.demNguocGuiThu !== null && this.demNguocGuiThu > 1) {
        this.demNguocGuiThu--;
        return;
      }
      clearInterval(this.demNguocTimer);
      this.demNguocTimer = undefined;
      // Firebase chỉ đưa push ra Windows khi tab KHÔNG hiển thị - ghi lại để kết quả nói rõ.
      const tabDangHienThi = document.visibilityState === 'visible';
      this.webPushService.guiThongBaoThu()
        .then(ketQua => this.hienKetQuaGuiThu(ketQua, tabDangHienThi))
        .catch(err => this.notification.error('Gửi thông báo thử thất bại', err?.message || 'Lỗi gọi API', { nzDuration: 0 }))
        .finally(() => (this.demNguocGuiThu = null));
    }, 1000);
  }

  /** Dev: hiện popup hệ thống qua service worker, không qua FCM - tách lỗi Windows/Chrome khỏi lỗi FCM. */
  thuPopupHeThong(): void {
    this.webPushService.thuPopupHeThong()
      .then(ketQua => this.notification.info('Thử popup Windows', ketQua, { nzDuration: 0 }))
      .catch(err => this.notification.error('Thử popup Windows thất bại', err?.message || String(err), { nzDuration: 0 }));
  }

  private hienKetQuaGuiThu(kq: KetQuaGuiThuPush, tabDangHienThi: boolean): void {
    const quyen = this.webPushService.quyenHienTai();
    const dong: string[] = [
      `Quyền trình duyệt: ${quyen}` + (quyen === 'granted' ? '' : ' → chưa cho phép, trình duyệt này sẽ không nhận push.'),
      `Trình duyệt này đã đăng ký token: ${kq.trinhDuyetDaDangKy ? 'có' : 'KHÔNG'}`,
      `Token của bạn trên backend: WEB ${kq.soToken['WEB'] ?? 0}, APP ${kq.soToken['APP_KINH_DOANH'] ?? 0}`,
      `FCM nhận thành công: ${kq.soGuiThanhCong}/${kq.tongToken}`
    ];
    kq.loi.forEach(l => dong.push(`Lỗi ${l.loaiToken ?? ''} ${l.token ?? ''}: ${l.maLoi ?? ''}`.trim()));
    if (kq.soTokenDaXoa > 0) {
      dong.push(`Đã tự xoá ${kq.soTokenDaXoa} token không còn hiệu lực (UNREGISTERED / SENDER_ID_MISMATCH).`);
    }
    dong.push(tabDangHienThi
      ? 'Lúc gửi, tab này ĐANG HIỂN THỊ → Firebase đưa push vào trang (toast "[Thông báo thử]"), Windows sẽ KHÔNG hiện popup. Thử lại và chuyển sang tab khác trong lúc đếm ngược.'
      : 'Lúc gửi, tab này đang ẩn → push phải hiện thành popup Windows.');
    if (kq.tongToken === 0) {
      dong.push('Backend không có token nào của bạn: kiểm tra bước đăng ký token (Network: POST firebase-tokens/web).');
    } else if (kq.soGuiThanhCong > 0 && !tabDangHienThi) {
      dong.push('FCM đã nhận. Không thấy popup → kiểm tra Windows: Settings → System → Notifications → Google Chrome, tắt Do not disturb.');
    }
    const thanhCong = kq.soGuiThanhCong > 0 && kq.loi.length === 0;
    this.notification[thanhCong ? 'success' : 'warning']('Kết quả gửi thông báo thử', dong.join('\n'),
      { nzDuration: 0, nzStyle: { whiteSpace: 'pre-line' } });
  }

  deSau(): void {
    this.notification.remove(KEY_LOI_MOI);
    try {
      localStorage.setItem(KHOA_HOAN_MOI_BAT, String(Date.now() + HOAN_MOI_BAT_MS));
    } catch {
      // Bỏ qua
    }
  }

  /**
   * Trình duyệt chỉ cho xin quyền khi người dùng bấm, nên sau đăng nhập hiện lời mời trong web;
   * bấm "Bật" mới gọi hộp thoại xin quyền của trình duyệt.
   */
  private capNhatNutBatThongBao(moiNeuCan = false): void {
    const quyen = this.webPushService.quyenHienTai();
    if (quyen !== 'default') {
      this.hienNutBatThongBao = false;
      this.thongBaoBiChan = false;
      if (quyen === 'denied') {
        this.webPushService.hoTro().then(ok => (this.thongBaoBiChan = ok));
      }
      return;
    }
    this.webPushService.hoTro().then(ok => {
      this.hienNutBatThongBao = ok;
      if (ok && moiNeuCan && !this.dangHoanMoi()) {
        this.notification.template(this.loiMoiBatThongBao, { nzKey: KEY_LOI_MOI, nzDuration: 0 });
      }
    });
  }

  private dangHoanMoi(): boolean {
    try {
      return Number(localStorage.getItem(KHOA_HOAN_MOI_BAT)) > Date.now();
    } catch {
      return false;
    }
  }

  laDaDoc(item: ThongBaoItem): boolean {
    return this.thongBaoService.laDaDoc(item);
  }

  moThongBao(item: ThongBaoItem): void {
    if (!this.laDaDoc(item)) {
      item.read = true;
      this.thongBaoService.danhDauDaDoc(item.id).subscribe({
        error: () => {
          item.read = false;
        }
      });
    }

    const dich = this.dichDieuHuong(item);
    if (!dich) return;
    this.isOpen = false;
    this.collapseNavbar.emit();
    this.router.navigate(dich.commands, { queryParams: dich.queryParams });
  }

  danhDauTatCa(): void {
    if (this.dangDanhDauTatCa || this.soChuaDoc === 0) return;
    this.dangDanhDauTatCa = true;
    this.thongBaoService.danhDauTatCaDaDoc().subscribe({
      next: () => {
        this.items.forEach(i => (i.read = true));
        this.dangDanhDauTatCa = false;
      },
      error: () => {
        this.dangDanhDauTatCa = false;
      }
    });
  }

  /**
   * routerLink backend lưu là URL trang danh sách của web cũ (không có id hồ sơ) nên tự điều hướng
   * theo role, ưu tiên role xử lý trực tiếp hồ sơ trước.
   */
  private dichDieuHuong(item: ThongBaoItem): { commands: string[]; queryParams?: Record<string, string | number> } | null {
    const idHSYC = item.idHSYC;
    if (!idHSYC) return null;
    // Hồ sơ vừa phân công, chờ chuyên viên tiếp nhận: mở thẳng tab Tiếp nhận để bấm nhận hồ sơ.
    // item.status là trạng thái hồ sơ lúc phát thông báo.
    if (this.kiemTraRole('ROLE_CHUYEN_VIEN_TD') && item.status === 'CHO_NV_TIEP_NHAN') {
      return {
        commands: ['/ho-so-phan-cong'],
        queryParams: { mode: 'tiep-nhan', statusHoSo: 'CHO_NV_TIEP_NHAN', status: 'DA_PHAN_CONG', idHoSo: idHSYC }
      };
    }
    // Các trạng thái từ khi tiếp nhận trở đi: CVTD, KSV, TDV ký, giám đốc thao tác/theo dõi ở quy trình thẩm định.
    if (ROLES_QUY_TRINH.some(r => this.kiemTraRole(r))) {
      return { commands: ['/quy-trinh-tham-dinh'], queryParams: { idHSYC } };
    }
    if (this.kiemTraRole('ROLE_PHAN_CONG')) {
      return { commands: ['/ho-so-phan-cong'], queryParams: { idHoSo: idHSYC } };
    }
    if (this.kiemTraRole('ROLE_HANH_CHINH_IN_AN')) {
      return { commands: ['/tai-lieu-in-an'] };
    }
    // Khớp menu navbar: NVKD / tín dụng xem hồ sơ ở "Hồ sơ yêu cầu" (list-form-customer).
    if (this.kiemTraRole('ROLE_NVKD') || this.kiemTraRole('ROLE_TIN_DUNG')) {
      return { commands: ['/list-form-customer'], queryParams: { idHoSo: idHSYC } };
    }
    return { commands: ['/ho-so-cua-toi'], queryParams: { idHoSo: idHSYC } };
  }

  thoiGianTuongDoi(item: ThongBaoItem): string {
    const ms = Number(item.timestamp);
    if (!ms) return item.createAt ?? '';
    const giay = Math.max(0, Math.floor((Date.now() - ms) / 1000));
    if (giay < 60) return 'Vừa xong';
    const phut = Math.floor(giay / 60);
    if (phut < 60) return `${phut} phút trước`;
    const gio = Math.floor(phut / 60);
    if (gio < 24) return `${gio} giờ trước`;
    const ngay = Math.floor(gio / 24);
    if (ngay < 30) return `${ngay} ngày trước`;
    return item.createAt?.split(' ')[0] ?? '';
  }

  trackById(_: number, item: ThongBaoItem): string {
    return item.id;
  }
}
